import React, { useMemo, useState } from 'react'
import cx from 'classnames'
import { Button, NoContent, Table, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { BillingAgencySetting } from '../../../api/accounting/types/BillingAgencySettingsResponse'
import { DiscountTarget } from '../DiscountPanel/DiscountPanel'
import styles from './BillingSettingsTable.module.scss'

type TProps = {
  settings: BillingAgencySetting[]
  /** The table-wide counter, so a row holding a different value stands out. */
  counter: number | null
  loading?: boolean
  onAddAgency: () => void
  onEditDiscounts: (target: DiscountTarget) => void
}

/** Stored as a fraction, e.g. 0.5455 is a 54.55% discount. Null is blank, which is not 0%. */
const formatDiscount = (value: number | null) => {
  if (value === null) return '—'
  return `${(value * 100).toFixed(2).replace(/\.00$/, '')}%`
}

// Falls back to the id only when the agency join found no name.
const displayName = ({ agencyName, agencyId }: BillingAgencySetting) => agencyName || agencyId

const BillingSettingsTable = ({ settings, counter, loading = false, onAddAgency, onEditDiscounts }: TProps) => {
  const [query, setQuery] = useState('')

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return settings
    // The id is no longer shown, but support staff paste one in far more often
    // than they type a name, so it stays searchable.
    return settings.filter(
      s =>
        displayName(s).toLowerCase().includes(term) ||
        (s.tradingAs ?? '').toLowerCase().includes(term) ||
        (s.notes ?? '').toLowerCase().includes(term) ||
        s.agencyId.includes(term),
    )
  }, [settings, query])

  const discounted = useMemo(
    () =>
      settings.filter(s => (s.percentProcessingFeeDiscount ?? 0) > 0 || (s.percentTransactionFeeDiscount ?? 0) > 0)
        .length,
    [settings],
  )

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          <span className={styles['summary-count']}>{settings.length}</span>
          <span className={styles['summary-label']}>
            {settings.length === 1 ? 'agency configured' : 'agencies configured'}
            {discounted > 0 && `, ${discounted} on a discount`}
          </span>
        </div>
        <div className={styles.tools}>
          <div className={styles.search}>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="agencyFilter"
                  autoComplete="off"
                  placeholder="Filter by agency"
                  value={query}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                />
              }
            />
          </div>
          <Button size="sm" onClick={onAddAgency}>
            Add agency
          </Button>
        </div>
      </div>

      {!loading && rows.length === 0 ? (
        <NoContent
          heading={settings.length === 0 ? 'No billing settings' : 'No matching agencies'}
          subHeading={
            settings.length === 0
              ? 'Nothing is configured in billing_agency_settings yet.'
              : 'Try a different name, note or agency id.'
          }
        />
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Cell head>Agency</Table.Cell>
              <Table.Cell head>Trading name</Table.Cell>
              <Table.Cell head className={styles.center}>
                Processing fee discount
              </Table.Cell>
              <Table.Cell head className={styles.center}>
                Transaction fee discount
              </Table.Cell>
              <Table.Cell head>Notes</Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {rows.map(setting => {
              // A row off the table-wide counter is the visible symptom of a
              // split table, so it gets called out.
              const isOutlier = counter !== null && setting.lastBillingInvoiceNumber !== counter
              return (
                <Table.Row
                  key={`${setting.agencyId}-${setting.asset}`}
                  className={styles.row}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    onEditDiscounts({
                      agencyId: setting.agencyId,
                      agencyName: displayName(setting),
                      tradingAs: setting.tradingAs,
                      existing: setting,
                    })
                  }
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    onEditDiscounts({
                      agencyId: setting.agencyId,
                      agencyName: displayName(setting),
                      tradingAs: setting.tradingAs,
                      existing: setting,
                    })
                  }}
                >
                  <Table.Cell>
                    <span className={styles.agency}>
                      <span className={styles['agency-name']}>
                        {displayName(setting)}
                        {/* The counter itself is on the card; here it only
                            matters which rows are off it. */}
                        {isOutlier && (
                          <span className={styles['outlier-tag']}>counter {setting.lastBillingInvoiceNumber}</span>
                        )}
                      </span>
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={cx({ [styles.muted]: !setting.tradingAs })}>{setting.tradingAs || '—'}</span>
                  </Table.Cell>
                  <Table.Cell className={styles.center}>
                    <span
                      className={cx(styles.numeric, { [styles.muted]: setting.percentProcessingFeeDiscount === null })}
                    >
                      {formatDiscount(setting.percentProcessingFeeDiscount)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className={styles.center}>
                    <span
                      className={cx(styles.numeric, { [styles.muted]: setting.percentTransactionFeeDiscount === null })}
                    >
                      {formatDiscount(setting.percentTransactionFeeDiscount)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.notes} title={setting.notes ?? undefined}>
                      {setting.notes}
                    </span>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      )}
    </div>
  )
}

export default BillingSettingsTable
